import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogTitle, Box, Typography,
  Chip, CircularProgress, Divider, IconButton, Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface ServicePreviewModalProps {
  serviceId: string;
  triggerLabel?: string; // e.g. "See what's included"
}

interface ServicePreview {
  id: string;
  name: string;
  description: string;
  brand_name: string;
  brand_logo_path?: string;
  brand_logo_url?: string;
  category: string;
  price_range_min?: number;
  price_range_max?: number;
}

export const ServicePreviewModal = ({ serviceId, triggerLabel = "See what's included →" }: ServicePreviewModalProps) => {
  const [open, setOpen] = useState(false);
  const [service, setService] = useState<ServicePreview | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && !service) {
      setLoading(true);
      fetch(`/api/marketplace/services/${serviceId}`)
        .then(r => r.json())
        .then(d => setService(d.service || d))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, serviceId, service]);

  const logoSrc = service?.brand_logo_url || service?.brand_logo_path;
  const priceMin = service?.price_range_min;
  const priceMax = service?.price_range_max;
  const priceDisplay = priceMin
    ? priceMin === priceMax || !priceMax
      ? `$${(priceMin / 100).toLocaleString()}/mo`
      : `$${(priceMin / 100).toLocaleString()}–$${(priceMax / 100).toLocaleString()}/mo`
    : null;

  return (
    <>
      <Typography
        component="span"
        variant="caption"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        sx={{
          color: 'primary.main',
          cursor: 'pointer',
          textDecoration: 'underline',
          fontSize: '0.75rem',
          '&:hover': { opacity: 0.8 },
        }}
      >
        {triggerLabel}
      </Typography>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          Included Service
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!loading && service && (
            <Stack spacing={2}>
              {/* Brand header */}
              <Box display="flex" alignItems="center" gap={1.5}>
                {logoSrc && (
                  <Box
                    component="img"
                    src={logoSrc}
                    alt={service.brand_name}
                    sx={{ height: 36, width: 'auto', maxWidth: 100, objectFit: 'contain' }}
                  />
                )}
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>{service.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{service.brand_name}</Typography>
                </Box>
                {service.category && (
                  <Chip label={service.category} size="small" sx={{ ml: 'auto' }} />
                )}
              </Box>

              <Divider />

              {/* Short description — first 300 chars */}
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                {service.description?.replace(/[#*_]/g, '').slice(0, 300)}
                {(service.description?.length ?? 0) > 300 ? '…' : ''}
              </Typography>

              {/* Price range */}
              {priceDisplay && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary">Starting from</Typography>
                  <Typography variant="subtitle2" fontWeight={700} color="primary.main">{priceDisplay}</Typography>
                </Box>
              )}

              {/* Link to full page */}
              <Box
                component="a"
                href={`/services/${serviceId}`}
                target="_blank"
                rel="noopener"
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  color: 'primary.main', fontSize: '0.8rem', textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                View full service details <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Box>
            </Stack>
          )}
          {!loading && !service && (
            <Typography color="text.secondary" variant="body2">Could not load service details.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
